import { useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import PercentIcon from '@mui/icons-material/Percent';
import CircleIcon from '@mui/icons-material/Circle';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TextFieldElement } from 'components/muiInputWrappers';
import { ConfigureIndicatorStatesRequest } from 'api/client';
import { validationRules } from 'helpers/utils';

export type FormFields = {
  '1': ConfigureIndicatorStatesRequest;
  '2': ConfigureIndicatorStatesRequest;
  '3': ConfigureIndicatorStatesRequest;
};

type Props = {
  handleClearInputs: () => void;
  form: UseFormReturn<FormFields>;
};

function Indicators({ form, handleClearInputs }: Props) {
  const theme = createTheme({
    palette: {
      warning: {
        main: '#EDA12E'
      }
    }
  });

  const watch1maxPercentage = Number(form.watch('1.maxPercentage'));
  const watch2maxPercentage = Number(form.watch('2.maxPercentage'));
  const watch2minPercentage = Number(form.watch('2.minPercentage'));

  const { t } = useTranslation('global');

  const { patternNumberWith2decimal } = validationRules();

  useEffect(() => {
    form.setValue(
      '2.minPercentage',
      Number((watch1maxPercentage + 0.01).toFixed(2))
    );
    form.setValue(
      '3.minPercentage',
      Number((watch2maxPercentage + 0.01).toFixed(2))
    );
  }, [watch1maxPercentage, watch2maxPercentage, form]);

  return (
    <ThemeProvider theme={theme}>
      <Grid
        justifyContent="space-evenly"
        margin={0}
        spacing={2}
        width="100%"
        container
      >
        <div style={{ width: 'auto' }}>
          <Typography alignItems="center" display="flex">
            &nbsp;
            <CircleIcon color="success" fontSize="small" />
            <span>&nbsp;{t('indicators.greenColor')}</span>
          </Typography>
          <FormControl
            sx={{
              m: 1,
              flexDirection: 'row',
              '@media (max-width: 1150px)': {
                width: '100%'
              },
              width: '80%'
            }}
            variant="outlined"
          >
            <TextFieldElement
              control={form.control}
              defaultValue="0"
              id="filled-start-adornment"
              InputProps={{
                endAdornment: <PercentIcon />
              }}
              label={t('indicators.shortenedMin')}
              name="1.minPercentage"
              type="number"
              variant="filled"
              disabled
              fullWidth
            />
          </FormControl>
          <FormControl
            sx={{
              m: 1,
              flexDirection: 'row',
              '@media (max-width: 1150px)': {
                width: '100%'
              },
              width: '80%'
            }}
            variant="outlined"
          >
            <TextFieldElement
              control={form.control}
              id="filled-start-adornment"
              InputProps={{
                endAdornment: <PercentIcon />
              }}
              label={t('indicators.shortenedMax')}
              name="1.maxPercentage"
              type="number"
              validation={{
                pattern: patternNumberWith2decimal,
                validate: (max: number) => {
                  if (max <= form.getValues('1.minPercentage') || max >= 100) {
                    return t('indicators.indicatorsValidation', {
                      higherThan: 0,
                      lessThan: 100
                    });
                  }
                  return true;
                }
              }}
              variant="filled"
              fullWidth
              required
            />
          </FormControl>
        </div>
        <div style={{ width: 'auto' }}>
          <Typography alignItems="center" display="flex">
            &nbsp;
            <CircleIcon color="warning" fontSize="small" />
            <span>&nbsp;{t('indicators.amberColor')}</span>
          </Typography>
          <FormControl
            sx={{
              m: 1,
              flexDirection: 'row',
              '@media (max-width: 1150px)': {
                width: '100%'
              },
              width: '80%'
            }}
            variant="outlined"
          >
            <TextFieldElement
              control={form.control}
              id="filled-start-adornment"
              InputProps={{
                endAdornment: <PercentIcon />
              }}
              label={t('indicators.shortenedMin')}
              name="2.minPercentage"
              type="number"
              variant="filled"
              disabled
              fullWidth
            />
          </FormControl>
          <FormControl
            sx={{
              m: 1,
              flexDirection: 'row',
              '@media (max-width: 1150px)': {
                width: '100%'
              },
              width: '80%'
            }}
            variant="outlined"
          >
            <TextFieldElement
              control={form.control}
              id="filled-start-adornment"
              InputProps={{
                endAdornment: <PercentIcon />
              }}
              label={t('indicators.shortenedMax')}
              name="2.maxPercentage"
              type="number"
              validation={{
                pattern: patternNumberWith2decimal,
                validate: (max: number) => {
                  if (max < watch2minPercentage || max >= 100) {
                    return t('indicators.indicatorsValidation', {
                      higherThan: watch2minPercentage,
                      lessThan: 100
                    });
                  }

                  return true;
                }
              }}
              variant="filled"
              fullWidth
              required
            />
          </FormControl>
        </div>
        <div style={{ width: 'auto' }}>
          <Typography alignItems="center" display="flex">
            &nbsp;
            <CircleIcon color="error" fontSize="small" />
            <span>
              &nbsp;
              {t('indicators.redColor')}
            </span>
          </Typography>
          <FormControl
            sx={{
              m: 1,
              flexDirection: 'row',
              '@media (max-width: 1150px)': {
                width: '100%'
              },
              width: '80%'
            }}
            variant="outlined"
          >
            <TextFieldElement
              control={form.control}
              id="filled-start-adornment"
              InputProps={{
                endAdornment: <PercentIcon />
              }}
              label={t('indicators.shortenedMin')}
              name="3.minPercentage"
              type="number"
              variant="filled"
              disabled
              fullWidth
            />
          </FormControl>
          <FormControl
            sx={{
              m: 1,
              flexDirection: 'row',
              '@media (max-width: 1150px)': {
                width: '100%'
              },
              width: '80%'
            }}
            variant="outlined"
          >
            <TextFieldElement
              control={form.control}
              defaultValue="100"
              id="filled-start-adornment"
              InputProps={{
                endAdornment: <PercentIcon />
              }}
              label={t('indicators.shortenedMax')}
              name="3.maxPercentage"
              type="number"
              variant="filled"
              disabled
              fullWidth
              required
            />
          </FormControl>
        </div>
      </Grid>

      <Grid justifyContent="center" spacing={2} container>
        <Grid lg={2} md={4} sm={6} xs={12} item>
          <Button
            onClick={handleClearInputs}
            size="large"
            sx={{ backgroundColor: '#ffffff', color: '#5F5F5F' }}
            type="reset"
            variant="outlined"
            fullWidth
          >
            {t('common.commonProperties.clear')}
          </Button>
        </Grid>
        <Grid lg={2} md={4} sm={6} xs={12} item>
          <Button
            size="large"
            sx={{ backgroundColor: '#1D6FCC', color: '#FFF' }}
            type="submit"
            variant="contained"
            fullWidth
          >
            {t('common.commonActions.accept')}
          </Button>
        </Grid>
      </Grid>
    </ThemeProvider>
  );
}

export default Indicators;
